import type { QuizData } from "@/types/quizData"
enum QuizStatuses {
  STARTED = "STARTED",
  COMPLETED = "COMPLETED",
  SUCCESS = "SUCCESS",
  ERROR = "ERROR"
}

type ErrorMessages = {
  400: string
  401: string
  403: string
  500: string
  DEFAULT: string
}

type ObjectWithStringKeys = { [key: string]: any }

export default class QuizService {
  // STATIC VARIABLES
  static ERROR_MESSAGES: ErrorMessages = {
    400: `<p>The quiz answers are invalid. Please contact us at <strong><a href="mailto:support@jobtest.org">support@jobtest.org</a></strong> with the same email address you used when completing your test to assist you.</p>`,
    401: `<p>Invalid credentials. Please contact us at <strong><a href="mailto:support@jobtest.org">support@jobtest.org</a></strong> with the same email address you used when completing your test to assist you.</p>`,
    403: `<p>This email has been already used to take the test. Since only one attempt is allowed per email, please use a different email to get your results.</p>`,
    500: `<p>We have encountered an internal error procesing your test. Please contact us at <strong><a href="mailto:support@jobtest.org">support@jobtest.org</a></strong> with the same email address you used when completing your test to assist you.</p>`,
    DEFAULT: `<p>There was an error on our end. Please contact us at <strong><a href="mailto:support@jobtest.org">support@jobtest.org</a></strong> with the same email address you used when completing your test to assist you.</p>`
  }

  static QUIZ_STATUSES = QuizStatuses

  static INDUSTRIES_MAP: ObjectWithStringKeys = {
    "Agriculture / Forestry / Fishing": 1,
    "Mining and Quarrying": 2,
    Manufacturing: 3,
    "Construction and Engineering": 4,
    "Wholesale and Retail Trade": 5,
    "Transportation and Logistics": 6,
    "Information and Communication Technology": 7,
    "Finance and Insurance": 8,
    "Real Estate and Property Management": 9,
    "Professional and Technical Services": 10,
    "Education and Training": 11,
    "Healthcare and Social Assistance": 12,
    "Arts / Entertainment / Recreation": 13,
    "Hospitality and Tourism": 14,
    "Government and Public Administration": 15,
    "Non-profit and Community Services": 16,
    "Legal Services": 17,
    "Media and Communication": 18,
    "Personal Services": 19,
    "Sales and Marketing": 20,
    "Science and Research": 21,
    "Sports and Fitness": 22,
    Telecommunications: 23,
    "Aviation and Aerospace": 24,
    "Startup and Innovation": 25
  }

  // STATIC METHODS
  /**
   * Get the RIASEC results that are generated by QUBL
   */
  static getRiasecFromResults(results: QuizData["results"]): ObjectWithStringKeys {
    if (!Array.isArray(results)) return {}

    const namedResults: ObjectWithStringKeys = {}
    const acceptedResults = ["realistic", "investigative", "artistic", "social", "enterprising", "conventional"]

    results.forEach((result) => {
      const resultName = result.metadata?.resultName
      if (!resultName) return
      if (acceptedResults.includes(resultName)) {
        const value = Number(result.points) || 0
        namedResults[resultName] = value
      }
    })

    return namedResults
  }

  /**
   * Loads the Qubl instance
   */
  static loadQublInstance(quizId: string, quizStyle: string): void {
    QuizService.loadQublBundle()
    const qublContainer = document.querySelector("#js-qubl") as HTMLElement
    const quizHtml = `<qubl-instance quiz-id="${quizId}" id="qubl-instance" css="${quizStyle}"></qubl-instance>`
    qublContainer.innerHTML = quizHtml
  }

  /**
   * Remove user answers that are not needed in the quiz data
   */
  static removeUserVariablesFromAnswers(answers: QuizData["answers"] | undefined) {
    if (!answers) return

    const answersToRemove = ["fullName", "email", "phoneNumber", "gender", "zipCode"]

    try {
      answersToRemove.forEach((answer) => {
        delete answers[answer]
      })

      return answers
    } catch (error) {
      console.error("Error removing answers", error)
      throw error
    }
  }

  // PRIVATE STATIC METHODS

  /**
   * Loads the Qubl instance bundle
   */
  private static loadQublBundle(): void {
    const script = "https://quiz.builders/instance-bundle"
    const target = document.createElement("script")
    const head = document.head
    target.setAttribute("src", script)
    target.setAttribute("async", "false")
    head.insertBefore(target, head.firstElementChild)
  }

  /**
   * Gets the industry id based on the string value
   */
  private static getIndustryValue(answer: string): number | null {
    if (!answer) return null
    return QuizService.INDUSTRIES_MAP[answer as keyof typeof this.INDUSTRIES_MAP] || null
  }

  /**
   * Gets the industry ids based on the string values
   */
  static getIndustryValues(answer: string | null = null): number[] {
    if (!answer) return []

    const answersArray = answer.split(",") || []
    const industryValues = answersArray.map((answer) => QuizService.getIndustryValue(answer))

    // Filter out null values
    return industryValues.filter((value): value is number => value !== null)
  }
}
